import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const RECEPTION_SESSIONS = {
  '07:30 - 11:00': { batDau: '07:30', ketThuc: '11:00' },
  '13:30 - 16:00': { batDau: '13:30', ketThuc: '16:00' },
};

const removeVietnameseAccents = (str) => {
  if (!str) return '';
  return String(str)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]/g, '_');
};

const formatTimeValue = (val) => {
  if (val === null || val === undefined || val === '') return '';
  if (typeof val === 'number') {
    // Excel time fraction (e.g. 0.33333 = 08:00)
    const totalMinutes = Math.round(val * 24 * 60);
    const h = Math.floor(totalMinutes / 60) % 24;
    const m = totalMinutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }
  const s = String(val).trim();
  if (s.includes(':')) {
    const parts = s.split(':');
    const h = parts[0]?.trim().padStart(2, '0');
    const m = parts[1]?.trim().padStart(2, '0');
    return `${h}:${m}`;
  }
  return s;
};

const formatDateValue = (val) => {
  if (val === null || val === undefined || val === '') return '';
  if (typeof val === 'number') {
    // Excel serial date (e.g. 45180)
    const date = new Date(Math.round((val - 25569) * 86400 * 1000));
    return dayjs(date).format('DD/MM/YYYY');
  }
  const s = String(val).trim();
  const vietnameseDate = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  if (vietnameseDate) {
    return `${vietnameseDate[1].padStart(2, '0')}/${vietnameseDate[2].padStart(2, '0')}/${vietnameseDate[3]}`;
  }
  const isoDate = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s);
  if (isoDate) {
    return `${isoDate[3].padStart(2, '0')}/${isoDate[2].padStart(2, '0')}/${isoDate[1]}`;
  }
  return s;
};

const isValidReceptionDate = (value) =>
  dayjs(value, 'DD/MM/YYYY', true).isValid();

export const parseScheduleExcelFile = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const rawRows = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

        const parsedRows = rawRows.map((row, index) => {
          const normalized = {};
          Object.keys(row).forEach((key) => {
            const normalizedKey = removeVietnameseAccents(key);
            normalized[normalizedKey] = row[key];
          });

          // Tìm các trường tương ứng
          const ngayRaw =
            normalized.ngay_tiep_dan ||
            normalized.ngay ||
            normalized.date ||
            normalized.ngay_lam_viec ||
            '';
          const tuRaw =
            normalized.tu ||
            normalized.gio_bat_dau ||
            normalized.bat_dau ||
            normalized.start_time ||
            normalized.from ||
            '';
          const denRaw =
            normalized.den ||
            normalized.gio_ket_thuc ||
            normalized.ket_thuc ||
            normalized.end_time ||
            normalized.to ||
            '';
          const caTiepDan = String(
            normalized.ca_tiep_dan || normalized.ca || normalized.session || '',
          ).trim();
          const selectedSession = RECEPTION_SESSIONS[caTiepDan] || null;
          const canBo =
            normalized.ten_can_bo ||
            normalized.ho_ten_can_bo ||
            normalized.can_bo ||
            normalized.leader ||
            normalized.officer ||
            'Cán bộ tiếp dân';
          const diaDiem =
            normalized.dia_diem ||
            normalized.location ||
            normalized.phong ||
            'Phòng tiếp công dân';
          const ghiChu =
            normalized.ghi_chu ||
            normalized.note ||
            normalized.purpose ||
            '';

          const formattedDate = formatDateValue(ngayRaw);
          const formattedTu = selectedSession?.batDau || formatTimeValue(tuRaw);
          const formattedDen = selectedSession?.ketThuc || formatTimeValue(denRaw);

          let isValid = true;
          let error = '';

          if (!formattedDate) {
            isValid = false;
            error = 'Thiếu ngày tiếp dân';
          } else if (!isValidReceptionDate(formattedDate)) {
            isValid = false;
            error = 'Ngày tiếp dân phải đúng định dạng dd/mm/yyyy';
          } else if (caTiepDan && !selectedSession) {
            isValid = false;
            error = 'Ca tiếp dân chỉ được là 07:30 - 11:00 hoặc 13:30 - 16:00';
          } else if (!formattedTu || !formattedDen) {
            isValid = false;
            error = 'Thiếu giờ tiếp dân';
          } else if (formattedTu >= formattedDen) {
            isValid = false;
            error = 'Giờ bắt đầu phải nhỏ hơn giờ kết thúc';
          }

          return {
            id: `row-${index}`,
            ngayTiepDan: formattedDate,
            batDau: formattedTu,
            ketThuc: formattedDen,
            caTiepDan,
            tenCanBo: String(canBo).trim(),
            diaDiem: String(diaDiem).trim(),
            ghiChu: String(ghiChu).trim(),
            isValid,
            error
          };
        });

        resolve(parsedRows);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = (err) => reject(err);
    reader.readAsArrayBuffer(file);
  });
};
